import { useState } from 'react';
import { api, ApiError } from '../../lib/api';
import type { Page, PublicElectionSummary, PublicationScope, PublicResultDetail, PublicResultSummary, PublicResultVerification } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

/**
 * GET /api/v1/m39/public/* — genuinely unauthenticated, no bearer token sent
 * (see lib/api.ts: it always attaches one if present, but these routes are
 * mounted before requireAuth in m39's app.ts so an absent/irrelevant token
 * doesn't matter). Used both pre-login (App.tsx's `/public/results` route,
 * no staff sign-in required — the whole point of a public results feed) and
 * inside the Shell as "Public Results Feed" so staff can see exactly what
 * citizens, press, and international observers see.
 */
export function PublicResults() {
  const [scope, setScope] = useState<PublicationScope | ''>('');
  const [electionId, setElectionId] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  const electionsQ = useApiQuery<Page<PublicElectionSummary>>('/api/v1/m39/public/elections?limit=50');
  const qp = new URLSearchParams({ limit: '50' });
  if (scope) qp.set('scope', scope);
  if (electionId) qp.set('electionId', electionId);
  const resultsQ = useApiQuery<Page<PublicResultSummary>>(`/api/v1/m39/public/results?${qp.toString()}`);

  return (
    <div>
      <PageHeader
        title="Public Results Feed"
        subtitle="Signed, independently verifiable — every certified result the Electoral Commission has published. Anyone can check the hash and signature below without an account."
      />

      <div className="card mb-6 p-5">
        <h2 className="mb-3 text-sm font-bold text-slate-700">Elections</h2>
        {electionsQ.loading ? (
          <Spinner />
        ) : (electionsQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No elections published yet" />
        ) : (
          <ul className="grid gap-2 sm:grid-cols-2">
            {electionsQ.data!.items.map((e) => (
              <li key={e.id} className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
                <div>
                  <div className="text-sm font-semibold text-slate-800">{e.title}</div>
                  <div className="text-xs text-slate-500">{e.type.replaceAll('_', ' ')}</div>
                </div>
                <StatusBadge status={e.state} />
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label="Election id (optional)" className="min-w-[220px] flex-1">
          <input className="field-input" value={electionId} onChange={(e) => setElectionId(e.target.value)} />
        </Field>
        <Field label="Scope" className="!mb-0">
          <select className="field-input !w-auto" value={scope} onChange={(e) => setScope(e.target.value as PublicationScope | '')}>
            <option value="">All</option>
            <option value="STATION">Station</option>
            <option value="SEAT">Seat</option>
            <option value="NATIONAL">National</option>
          </select>
        </Field>
      </div>

      {resultsQ.error ? <ErrorNote message={resultsQ.error} /> : null}
      <div className="card overflow-x-auto">
        {resultsQ.loading ? (
          <Spinner />
        ) : (resultsQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No published results match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Scope</th>
                <th className="table-head">Outcome</th>
                <th className="table-head">Valid votes</th>
                <th className="table-head">Payload hash</th>
              </tr>
            </thead>
            <tbody>
              {resultsQ.data!.items.map((r) => (
                <tr key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelected(r.id)}>
                  <td className="table-cell font-medium">{r.scope}</td>
                  <td className="table-cell text-xs">{r.outcome ? <StatusBadge status={r.outcome} /> : '—'}</td>
                  <td className="table-cell text-xs tabular-nums">{r.totalValidVotes}</td>
                  <td className="table-cell font-mono text-[11px] text-slate-400">{r.payloadHash.slice(0, 20)}…</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected ? <PublicResultModal id={selected} onClose={() => setSelected(null)} /> : null}
    </div>
  );
}

function PublicResultModal({ id, onClose }: { id: string; onClose: () => void }) {
  const detailQ = useApiQuery<PublicResultDetail>(`/api/v1/m39/public/results/${id}`);
  const [verification, setVerification] = useState<PublicResultVerification | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const verify = async () => {
    setBusy(true);
    setVerifyError(null);
    try {
      const res = await api.get<PublicResultVerification>(`/api/v1/m39/public/results/${id}/verify`);
      setVerification(res);
    } catch (e) {
      setVerifyError(e instanceof ApiError ? e.message : 'Verification request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Published result" onClose={onClose} wide>
      {detailQ.loading ? (
        <Spinner />
      ) : detailQ.error ? (
        <ErrorNote message={detailQ.error} />
      ) : detailQ.data ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <div>
              <div className="field-label">Scope</div>
              {detailQ.data.scope}
            </div>
            <div>
              <div className="field-label">Outcome</div>
              {detailQ.data.outcome ? <StatusBadge status={detailQ.data.outcome} /> : '—'}
            </div>
            <div>
              <div className="field-label">Registered / valid / rejected</div>
              {detailQ.data.payload.totals.registeredVoters} / {detailQ.data.payload.totals.validVotes} / {detailQ.data.payload.totals.rejectedVotes}
            </div>
            <div>
              <div className="field-label">Certified at</div>
              {detailQ.data.payload.certifiedAt}
            </div>
          </div>

          {detailQ.data.payload.candidates.length > 0 ? (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Candidate</th>
                  <th className="table-head text-right">Votes</th>
                </tr>
              </thead>
              <tbody>
                {detailQ.data.payload.candidates
                  .slice()
                  .sort((a, b) => b.votes - a.votes)
                  .map((c) => (
                    <tr key={c.nominationId}>
                      <td className="table-cell font-medium">{c.candidateName}</td>
                      <td className="table-cell text-right tabular-nums">{c.votes}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          ) : null}

          <div className="rounded-lg border border-slate-200 p-3 text-xs">
            <div className="field-label">Payload hash</div>
            <div className="break-all font-mono">{detailQ.data.payloadHash}</div>
            <div className="field-label mt-2">Signature</div>
            <div className="break-all font-mono">{detailQ.data.signature}</div>
          </div>

          {verifyError ? <ErrorNote message={verifyError} /> : null}
          {verification ? (
            <div className={`rounded-lg border p-3 text-sm ${verification.hashMatches && verification.signatureValid ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-rose-200 bg-rose-50 text-rose-800'}`}>
              Hash match: {verification.hashMatches ? 'yes' : 'no'} · Signature valid: {verification.signatureValid ? 'yes' : 'no'}
            </div>
          ) : null}
          <button className="btn-secondary" onClick={verify} disabled={busy}>
            {busy ? 'Verifying…' : 'Recompute hash & verify signature'}
          </button>
        </div>
      ) : null}
    </Modal>
  );
}
